import { AxiosResponse } from "axios";
import api from "./api/api";
import { FinalSalary } from "./finalSalary";
import { PensionPot } from "./pensionPot";
import { Retiree } from "./retiree";
import { Saving } from "./saving";
import { OtherCash } from "./otherCash";
import { OtherIncome } from "./otherIncome";
import store from "./store";
import View from "./view";
import { Configuration } from "./configuration/configuration";
import Monitoring from "./monitoring/monitoring";
import { ProviderCharges } from "./providerCharges";
import ScenarioType from "./scenarioType";
import { Scenario } from "./scenario";
import ObjectBuilder from "./objectBuilder";
import ScenarioOutcome from "./scenarioOutcome";
import Lifestyle from "./lifestyle";
import { SubscribeToGuaranteedIncomeResponse } from "./response/subscribeToGuaranteedIncomeResponse";
import ResponseTracker from "../../shared/ts/responseTracker";
import { MessageResponse } from "./response/messageResponse";
import { RetirementPlanResponse } from "./response/retirementPlanResponse";
import HttpStatusCode from "../../shared/ts/httpStatusCode";
import HealthState from "./healthState";
import { AccountSearchResponse } from "./response/accountSearchResponse";
import Constants from "./constants";
import { ensureCssLoaded, ensureMetaTagPresent, ensureScriptLoaded, hasConsented, isImpersonating, presentValueOfPensionsAndSavings } from "./utilities";
import { nextTick } from "vue";
import TakeLessResponse from "./solve/takeLessResponse";
import PayMoreResponse from "./solve/payMoreResponse";
import User from "./user";
import { getRequestContext } from "./api/apiUtilities";
import EquityReleaseResponse from "./solve/equityReleaseResponse";
import { EquityReleaseRateResponse } from "./response/equityReleaseRateResponse";
import { BeginPensionTransferResponse } from "./response/beginPensionTransferResponse";
import { AnnuityProvidersResponse } from "./response/annuityProvidersResponse";
import { AnnuityProviderResponse } from "./response/annuityProviderResponse";
import { AdvisersResponse } from "./response/advisersResponse";
import { AdviserResponse } from "./response/adviserResponse";
import Result from "./result";
import ConsentType from "./consent/consentType";
import ExternalAgency from "./consent/externalAgency";
import PermittedEntityType from "./consent/permittedEntityType";
import { AdditionalPersonalInfo } from "./additionalPersonalInfo";
import { Country } from "./country";
import { PensionProvidersResponse } from "./response/pensionProvidersResponse";
import { PensionProviderResponse } from "./response/pensionProviderResponse";
import { ApplicationInfo } from "./secure/applicationInfo";
import AccountRegistrationBehaviour from "./accountRegistrationBehaviour";
import FinalSalaryOption from "./assets/finalsalary/finalSalaryOption";
import { Notification as AppNotification } from "./notifications/notification";
import NotificationConcern from "./notifications/notificationConcern";
import FinalSalarySchemeOptionsDetailResponse from "./response/finalSalarySchemeOptionsDetailResponse";
import { RedirectResponse } from "./response/redirectResponse";
import dateTimeProvider from "./systemDateTimeProvider";

class App {
  public async initialise(isAnon = false) {
    const configElement = document.getElementById("config");
    let config: Configuration;
    if (configElement) {
      config = JSON.parse(configElement.innerHTML);
    } else {
      config = await this.retrieveConfig();
    }

    store.commit("updateConfiguration", config);
    store.commit("updateView", config.activeView);

    Monitoring.startLogging(config.aiInstrumentationKey);

    if (config.monitor) {
      Monitoring.startGA();
      Monitoring.startFacebookPixel();
      Monitoring.startBing();
      Monitoring.startHotjar(config.hotjarSiteId);
      Monitoring.startMoosend();

      ensureMetaTagPresent('google-site-verification', 'AF5Zu_0fNdKbAOxt6rBVxcLqv1uBhItlCA_NFATlf_c');
    }

    if (config.olarkEnabled) {
      Monitoring.startOlark();
    }

    if (isAnon) {
      if (config.requestCookieConsent) {
        ensureCssLoaded('https://cdnjs.cloudflare.com/ajax/libs/cookieconsent2/3.0.3/cookieconsent.min.css');
        ensureScriptLoaded("https://cdnjs.cloudflare.com/ajax/libs/cookieconsent2/3.0.3/cookieconsent.min.js", () => {
          if (document.readyState === 'complete') {
            this.initialiseCookie();
          } else {
            window.addEventListener('load', this.initialiseCookie);
          }
        });
      }
    } else {
      const idObtained = await this.getUserInfo();

      if (!idObtained) {
        window.location.href = Constants.LoginResouce;
        return;
      }

      await this.getRetiree();

      const userProperties: Record<string, string> = this.getGoogleAnalyticsUserProperties(config.site);
      Monitoring.gaSetUserProperties(store.state.user.id, userProperties);
    }

    Monitoring.gaSendPageView();
  }

  public async retrieveConfig(): Promise<Configuration> {
    const axiosResponse: AxiosResponse = await api.getConfig();
    return axiosResponse.data as Configuration;
  }

  public async updatePersonalDetail(
    dateOfBirth: Date,
    sex: string,
    alreadyDrawingPension: boolean,
    retirementDate: Date
  ): Promise<Result> {
    if (isImpersonating()) {
      return new Result(true);
    }

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.updatePersonalDetail(
        dateOfBirth,
        sex,
        alreadyDrawingPension,
        retirementDate
      );
    }, false);

    return this.mapMessageResponseToResult(messageResponse);
  }

  initialiseCookie () {
    (window as any).cookieconsent.initialise({
        "palette": {
            "popup": {
                "background": "#000"
            },
            "button": {
                "background": "#f1d600"
            }
        },
        "content": {
            "href": "home/cookiepolicy"
        },
        "position": "bottom-left"
    });
  }

  public async updateRequirements(
    takingDCTaxFreeAsLumpSum: boolean,
    livesInLondon: boolean,
    supportingPartner: boolean,
    lifestyle: Lifestyle,
    afterTaxIncome: number,
    extraIncome: number,
    increaseIncomeWithInflation: boolean,
    increaseIncomeWithInflationDuration: string,
    income: Array<number>
  ): Promise<ResponseTracker<MessageResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkMessageResponse());
    }

    const messageResponse = await api.updateRequirements(
      takingDCTaxFreeAsLumpSum,
      livesInLondon,
      supportingPartner,
      lifestyle,
      afterTaxIncome,
      extraIncome,
      increaseIncomeWithInflation,
      increaseIncomeWithInflationDuration,
      income
    );

    const responseTracker = ResponseTracker.fromMessageResponse(
      messageResponse
    );

    if (responseTracker.isOk) {
      this.getRetiree();
    }

    return Promise.resolve<ResponseTracker<MessageResponse>>(responseTracker);
  }

  public async updateStatePension(amount: number): Promise<ResponseTracker<MessageResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkMessageResponse());
    }

    const messageResponse = await api.updateStatePension(amount);

    const responseTracker = ResponseTracker.fromMessageResponse(
      messageResponse
    );

    if (responseTracker.isOk) {
      this.getRetiree();
    }

    return Promise.resolve<ResponseTracker<MessageResponse>>(responseTracker);
  }

  public async refineStatePension(
    amount: number,
    pastContributionYears: number,
    futureContributionYears: number,
    copeDeduction: number
  ): Promise<ResponseTracker<MessageResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkMessageResponse());
    }

    const messageResponse = await api.refineStatePension(
      amount,
      pastContributionYears,
      futureContributionYears,
      copeDeduction
    );

    const responseTracker = ResponseTracker.fromMessageResponse(
      messageResponse
    );

    if (responseTracker.isOk) {
      this.getRetiree();
    }

    return Promise.resolve<ResponseTracker<MessageResponse>>(responseTracker);
  }

  public async undoRefineStatePension(): Promise<ResponseTracker<MessageResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkMessageResponse());
    }

    const messageResponse = await api.undoRefineStatePension();

    const responseTracker = ResponseTracker.fromMessageResponse(
      messageResponse
    );

    if (responseTracker.isOk) {
      this.getRetiree();
    }

    return Promise.resolve<ResponseTracker<MessageResponse>>(responseTracker);
  }

  public async updatePensionPot(
    pensionPot: PensionPot
  ): Promise<ResponseTracker<AxiosResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    const axiosResponse: AxiosResponse = await api.updatePensionPot(pensionPot);

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      true
    );

    if (!responseTracker.isOk) {
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    this.getRetiree();

    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async updateFinalSalary(
    finalSalary: FinalSalary
  ): Promise<ResponseTracker<AxiosResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    const axiosResponse: AxiosResponse = await api.updateFinalSalary(
      finalSalary
    );

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      true
    );

    if (!responseTracker.isOk) {
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    this.getRetiree();

    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async updateSaving(
    saving: Saving
  ): Promise<ResponseTracker<AxiosResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    const axiosResponse: AxiosResponse = await api.updateSaving(saving);

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      true
    );

    if (!responseTracker.isOk) {
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    this.getRetiree();

    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async updateOtherCash(
    otherCash: OtherCash
  ): Promise<ResponseTracker<AxiosResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    const axiosResponse: AxiosResponse = await api.updateOtherCash(otherCash);

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      true
    );

    if (!responseTracker.isOk) {
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    this.getRetiree();

    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async updateOtherIncome(
    otherIncome: OtherIncome
  ): Promise<ResponseTracker<AxiosResponse>> {
    if (isImpersonating()) {
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    const axiosResponse: AxiosResponse = await api.updateOtherIncome(
      otherIncome
    );

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      true
    );

    if (!responseTracker.isOk) {
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    this.getRetiree();

    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async deletePensionPot(id: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }
    await api.deletePensionPot(id);
    this.getRetiree();
  }

  public async deleteFinalSalary(id: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }
    await api.deleteFinalSalary(id);
    this.getRetiree();
  }

  public async deleteOtherSavings(id: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }
    await api.deleteOtherSavings(id);
    this.getRetiree();
  }

  public async deleteOtherCash(id: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }
    await api.deleteOtherCash(id);
    this.getRetiree();
  }

  public async deleteOtherIncome(id: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }
    await api.deleteOtherIncome(id);
    this.getRetiree();
  }

  public async getProviderCharges(): Promise<ProviderCharges | null> {
    const axiosResponse: AxiosResponse = await api.getProviderCharges();

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      false
    );

    if (!responseTracker.isOk) {
      return null;
    }

    return axiosResponse.data as ProviderCharges;
  }

  public async transferToPensionLab(): Promise<ResponseTracker<AxiosResponse>> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      alert("Transfer to live dashboard not possible when viewing account.");
      return Promise.resolve(ResponseTracker.asFakeOkAxiosResponse());
    }

    let returnToView = "";
    switch (store.state.view) {
      case View.Haves:
        returnToView = "income";
        break;
      case View.Dashboard:
        returnToView = "dashboard";
        break;
      case View.GuiidePlus:
        returnToView = "guiideplus";
        break;
      default:
        returnToView = "dashboard";
    }

    const axiosResponse: AxiosResponse = await api.transferToPensionLab(
      returnToView
    );

    const responseTracker = new ResponseTracker<AxiosResponse>(axiosResponse);
    const guiideResponse = axiosResponse.data;
    const guiideResponseCode = guiideResponse.Success === true ? 200 : 400;
    responseTracker.update(guiideResponseCode, guiideResponse.message);

    if (!responseTracker.hasResponse) {
      alert("Unable to transfer to dashboard as no response received.");
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    if (!responseTracker.isOk) {
      alert("Unable to transfer to dashboard.");
      return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
    }

    window.location = axiosResponse.data.RedirectUrl;
    return Promise.resolve<ResponseTracker<AxiosResponse>>(responseTracker);
  }

  public async checkIfCanSolveByTakingLess(scenarioType: ScenarioType): Promise<TakeLessResponse> {
    return await api.checkIfCanSolveByTakingLess(scenarioType);
  }

  public async checkIfCanSolveByPayingMore(scenarioType: ScenarioType): Promise<PayMoreResponse> {
    return await api.checkIfCanSolveByPayingMore(scenarioType);
  }

  public async checkIfCanSolveByEquityRelease(scenarioType: ScenarioType): Promise<EquityReleaseResponse> {
    return await api.checkIfCanSolveByEquityRelease(scenarioType);
  }

  public async solveByReducingIncome(reduceByPercent: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    const response = await api.solveByTakingLess(reduceByPercent);

    if (response) {
      this.getRetiree();
    }

    return;
  }

  public async solveByIncreasingPayments(
    monthlyPayment: number
  ): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    const response = await api.solveByIncreasingPayments(monthlyPayment);

    if (response) {
      this.getRetiree();
    }

    return Promise.resolve();
  }

  public async solveByReleasingEquity(amount: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    const response = await api.solveByEquityRelease(amount);

    if (response) {
      Monitoring.gaEvent(
        "EquityRelease",
        amount
      );

      this.getRetiree();
    }

    return Promise.resolve();
  }

  public async applyGuaranteedIncome(
    ageAtSubscription: number | null,
    percentageToAllocate: number | null,
    eventSource: string
  ): Promise<ResponseTracker<SubscribeToGuaranteedIncomeResponse>> {
    if (isImpersonating()) {
      throw new Error("Invalid operation.");
    }

    const subscribeToGuaranteedIncomeResponse = await api.applyGuaranteedIncome(
      ageAtSubscription,
      percentageToAllocate
    );

    const responseTracker = ResponseTracker.fromMessageResponse(
      subscribeToGuaranteedIncomeResponse
    );

    if (responseTracker.isOk) {
      Monitoring.gaEvent(
        eventSource,
        subscribeToGuaranteedIncomeResponse.purchasePrice
      );
      this.getRetiree();
    }

    return Promise.resolve<
      ResponseTracker<SubscribeToGuaranteedIncomeResponse>
    >(responseTracker);
  }

  public async updateGuaranteedIncome(
    ageAtSubscription: number,
    percentToAllocate: number
  ): Promise<ResponseTracker<MessageResponse>> {
    if (isImpersonating()) {
      throw new Error("Invalid operation.");
    }

    const subscribeToGuaranteedIncomeResponse = await api.updateGuaranteedIncome(
      ageAtSubscription,
      percentToAllocate
    );

    const responseTracker = ResponseTracker.fromMessageResponse(
      subscribeToGuaranteedIncomeResponse
    );

    if (responseTracker.isOk) {
      Monitoring.gaEvent(
        "GIUpdatedPlan",
        subscribeToGuaranteedIncomeResponse.purchasePrice
      );
      this.getRetiree();
    }

    return Promise.resolve<
      ResponseTracker<SubscribeToGuaranteedIncomeResponse>
    >(responseTracker);
  }

  public async deleteGuaranteedIncome(): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await api.deleteGuaranteedIncome();
    this.getRetiree();
    return Promise.resolve();
  }

  public async setGuaranteedIncomeNotificationStatus(
    notify: boolean
  ): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await api.setGuaranteedIncomeNotificationStatus(notify);
    await this.getRetiree();
    return;
  }

  public async updateProtectedPot(protectedPot: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await this.asTransactionOngoing(async () => {
      return await api.updateProtectedPot(protectedPot);
    }, true);
    return;
  }

  public async updatePensionsRate(rateOfReturn: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await this.asTransactionOngoing(async () => {
      return await api.updatePensionReturnRate(rateOfReturn);
    }, true);
    return;
  }

  public async updateSavingsRate(rateOfReturn: number): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await this.asTransactionOngoing(async () => {
      return await api.updateSavingsReturnRate(rateOfReturn);
    }, true);
    return;
  }

  public async updateHealthState(healthState: HealthState): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await this.asTransactionOngoing(async () => {
      return await api.updateHealthState(healthState);
    }, true);
    return;
  }

  public async updateSurvivabilityState(): Promise<void> {
    if (isImpersonating()) {
      return;
    }

    await this.asTransactionOngoing(async () => {
      return await api.updateSurvivabilityState();
    }, true);
    return;
  }

  public async getYearsToLiveFromRetirementOn50thSurvivalPercentile(): Promise<number> {
    if (isImpersonating()) {
      return 0;
    }

    return api.getYearsToLiveFromRetirementOn50thSurvivalPercentile();
  }

  public async getFinalSalaryOptions(schemeId: number): Promise<FinalSalarySchemeOptionsDetailResponse> {
    return await this.asTransactionOngoing<FinalSalarySchemeOptionsDetailResponse>(async () => {
      return (await api.getFinalSalaryOptions(schemeId)).data;
    }, false);
  }

  public async findAccounts(filter: string): Promise<AccountSearchResponse> {
    return await this.asTransactionOngoing<AccountSearchResponse>(async () => {
      return await api.findAccounts(filter);
    }, false);
  }

  public async getEquityReleaseRate(age: number): Promise<EquityReleaseRateResponse> {
    return await this.asTransactionOngoing<EquityReleaseRateResponse>(async () => {
      return await api.getEquityReleaseRate(age);
    }, false);
  }

  public async getPensionProviders() {
    const pensionProviderResponse = await this.asTransactionOngoing<PensionProvidersResponse>(async () => {
      return await api.getPensionProviders();
    }, false);

    const pensionProviders = pensionProviderResponse.status == HttpStatusCode.Ok ? pensionProviderResponse.providers : new Array<PensionProviderResponse>();

    const forCostComparison = this.createPensionProviderList(pensionProviders, pensionProviderResponse.costComparisonOrder);
    const forFindYourself = this.createPensionProviderList(pensionProviders, pensionProviderResponse.findYourselfOrder);

    store.commit("updatePensionProvidersForCostComparison", forCostComparison);
    store.commit("updatePensionProvidersForFindYourself", forFindYourself);
  }

  public async getAnnuityProviders() {
    const annuityProviderResponse = await this.asTransactionOngoing<AnnuityProvidersResponse>(async () => {
      return await api.getAnnuityProviders();
    }, false);

    const annuityProviders = annuityProviderResponse.status == HttpStatusCode.Ok ? annuityProviderResponse.providers : new Array<AnnuityProviderResponse>();
    store.commit("updateAnnuityProviders", annuityProviders);
  }

  public async getAdvisers() {
    const advisersResponse = await this.asTransactionOngoing<AdvisersResponse>(async () => {
      return await api.getAdvisers();
    }, false);

    const advisers = advisersResponse.status == HttpStatusCode.Ok ? advisersResponse.advisers : new Array<AdviserResponse>();
    store.commit("updateAdvisers", advisers);
  }

  public async getUserInfo(): Promise<boolean> {
    try {
      let user: User | null = null;
      let tries = 3;

      const requestContext = getRequestContext();

      while (tries > 0 && user == null) {
        user = await api.getUser(requestContext.isImpersonating, (user) => { store.commit("updateUser", user) });

        if (user) {
          dateTimeProvider.sync(new Date(user.systemDate));
        }

        tries--;
      }

      if (user == null) {
        window.location.href = Constants.LoginResouce;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  public downloadReport() {
    api.downloadReport();
  }

  public async beginPensionTransfer(applicationInfo: ApplicationInfo): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(false, "Transfer to pension provider not possible when viewing account.");
    }

    const pensionTransferResponse = await this.asTransactionOngoing<BeginPensionTransferResponse>(async () => {
      return await api.beginPensionTransfer(applicationInfo);
    }, false);

    if (pensionTransferResponse.status != HttpStatusCode.Ok) {
      return new Result(false, pensionTransferResponse.message);
    }

    window.location.href = pensionTransferResponse.redirectTo;

    // const newWindow = window.open(pensionTransferResponse.redirectTo, '_blank');
    // if (newWindow) {
    //   newWindow.focus();
    // } else {
    //   window.location.href = pensionTransferResponse.redirectTo;
    // }

    return new Result(true);
  }

  public async grantConsent(permittedEntityType: PermittedEntityType, granteeId: ExternalAgency, consentType: ConsentType,
      refreshRetireeIfOk: boolean): Promise<Result> {
    if (hasConsented(granteeId, consentType)) {
      return new Result(true);
    }

    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(true);
    }

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.grantConsent(permittedEntityType, granteeId, consentType);
    }, refreshRetireeIfOk);

    return this.mapMessageResponseToResult(messageResponse);
  }

  public async revokeConsent(permittedEntityType: PermittedEntityType, granteeId: ExternalAgency, consentType: ConsentType): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(true);
    }

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.revokeConsent(permittedEntityType, granteeId, consentType);
    }, true);

    return this.mapMessageResponseToResult(messageResponse);
  }

  public async updateAdditionalPersonalInfo(additionalInfo: AdditionalPersonalInfo): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(true);
    }

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.updateAdditionalPersonalInfo(additionalInfo);
    }, false);

    return this.mapMessageResponseToResult(messageResponse);
  }

  public async updateFinalSalaryOption(finalSalaryOption: FinalSalaryOption, taxFreeCashLumpSumAmount: number, immediateSpendAmount: number): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(true);
    }

    const refreshRetireeIfOk = true;

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.updateFinalSalaryOption(finalSalaryOption, taxFreeCashLumpSumAmount, immediateSpendAmount);
    }, refreshRetireeIfOk);

    return this.mapMessageResponseToResult(messageResponse);
  }

  public async getCountries(): Promise<Array<Country>> {
    if (store.state.countries.length > 0) {
      return store.state.countries;
    }

    const getCountriesResult = await api.getCountries();

    if (!getCountriesResult.successful) {
      return store.state.countries;
    }

    store.commit("updateCountries", getCountriesResult.data);
    return getCountriesResult.data;
  }

  public async getNotifications(notificationConcern: NotificationConcern): Promise<Array<AppNotification>> {
    if (store.state.notifications.length > 0) {
      return store.state.notifications;
    }

    const getNotificationsResult = await api.getNotifications(notificationConcern);

    if (!getNotificationsResult.successful) {
      return store.state.notifications;
    }

    store.commit("updateNotifications", getNotificationsResult.data);
    return getNotificationsResult.data;
  }

  public async getRetiree(withinTransactionOngoing = false, forceViewReload = false): Promise<Retiree | null> {
    if (!withinTransactionOngoing) {
      return await this.getRetireeWorker();
    }

    return await this.asTransactionOngoing(async () => {
      if (forceViewReload) {
        const currentView = store.state.view;
        store.commit("updateView", View.NoView);
        await nextTick();
        await store.dispatch("retireeDataChanged", null);
        store.commit("updateView", currentView);
        await nextTick();
      }

      await this.getRetireeWorker();
    }, false);
  }

  private async getRetireeWorker(): Promise<Retiree | null> {
    const requestContext = getRequestContext();
    const axiosResponse: AxiosResponse = await api.getRetiree();

    const responseTracker = ResponseTracker.fromAxiosResponse(
      axiosResponse,
      false
    );

    await store.dispatch("updateGetRetireeResponse", responseTracker);

    if (!responseTracker.isOk) {
      return null;
    }

    const retiree = axiosResponse.data as Retiree;

    await store.dispatch("retireeDataChanged", retiree);

    const returnScenarioPromise = (scenarioType: ScenarioType) => {
      if (requestContext.isImpersonating || this.isScenarioActiveInCurrentView(scenarioType)) {
        return api.runScenario(scenarioType, false);
      } else {
        const unknownOutcome = ObjectBuilder.createScenario(
          scenarioType,
          ScenarioOutcome.Unknown
        );
        return Promise.resolve<Scenario>(unknownOutcome);
      }
    };

    const returnChartPromise = () => {
      if (requestContext.isImpersonating || this.isChartActiveInCurrentView()) {
        return api.getChartData();
      } else {
        return Promise.resolve<[]>([]);
      }
    };

    const returnPlanPromise = () => {
      if (requestContext.isImpersonating || this.isPlanActiveInCurrentView()) {
        return app.getRetirementPlan();
      } else {
        return Promise.resolve<null>(null);
      }
    };

    const returnFinishInfoPromise = () => {
      if (requestContext.isImpersonating || this.isFinishInfoActiveInCurrentView()) {
        return api.getFinishInfo();
      } else {
        return Promise.resolve<null>(null);
      }
    }

    await Promise.all([
      returnScenarioPromise(ScenarioType.AccordingToPlan),
      returnScenarioPromise(ScenarioType.LowerGrowth),
      returnScenarioPromise(ScenarioType.LiveThreeYearsLonger),
      returnScenarioPromise(ScenarioType.PotsFallTwentyPercent),
      returnScenarioPromise(ScenarioType.NeedExtraMoneyForLastTwoYears),
      returnChartPromise(),
      returnPlanPromise(),
      returnFinishInfoPromise()
    ]).then(responses => {
      const [
        accordingToPlanResponse,
        lowerGrowthResponse,
        liveLongerResponse,
        marketCrashResponse,
        needCareResponse,
        chartDataResponse,
        planResponse,
        finishInfoResponse
        ] = responses;
      store.commit("updateAccordingToPlanScenario", accordingToPlanResponse);
      store.commit("updateLowerGrowthScenario", lowerGrowthResponse);
      store.commit("updateYouLiveLongerScenario", liveLongerResponse);
      store.commit("updateMarketCrashScenario", marketCrashResponse);
      store.commit("updateNeedSomeCareScenario", needCareResponse);
      store.commit("updateAnnualIncomeSummaryData", chartDataResponse.annualIncomeSummary);
      store.commit("updatePotsSummaryData", chartDataResponse.potsSummary);
      store.commit("updateAnnualIncomeByTypeData", chartDataResponse.annualIncomeByType);
      store.commit("updateAnnualIncomeBySourceData", chartDataResponse.annualIncomeBySource);
      store.commit("updatePlan", planResponse);
      store.commit("updateFinishInfo", finishInfoResponse);
    });

    return retiree;
  }

  public async runAllTestScenariosForGuaranteedIncome(): Promise<Array<Scenario>> {
    return await api.runAllTestScenariosForGuaranteedIncome();
  }

  public async registerEmail(email: string, phoneNumber: string): Promise<MessageResponse> {
    const registerEmailResponse = await api.registerEmail(email, phoneNumber);

    const successful = registerEmailResponse.status == HttpStatusCode.Ok ? "Yes" : "No";

    let name = "registeredEmail";
    let payload: any = {
      successful
    };

    if (store.state.configuration.signupBehaviour == AccountRegistrationBehaviour.OnlyEmailReport) {
      if (phoneNumber?.trim()) {
        Monitoring.trackEvent("getMyPlanTel", {});
      }

      let emailPlanSuccessfulSendTelValue = successful;
      if (!phoneNumber?.trim()) {
        emailPlanSuccessfulSendTelValue = "nonumber";
      }

      name = "getMyPlanEmail";
      payload = {
        emailPlanSuccessfulSend: successful,
        emailPlanSuccessfulSendTel: emailPlanSuccessfulSendTelValue
      };
    }

    Monitoring.trackEvent(name, payload);

    if (registerEmailResponse.status == HttpStatusCode.Ok) {
      store.state.retiree.isRegistered = true;
      store.commit("updateHasStartedRegistration", true);
      const valueOfAllSavingsAndPots = presentValueOfPensionsAndSavings();
      Monitoring.gaEvent('Subscribe', valueOfAllSavingsAndPots);
    }

    return registerEmailResponse;
  }

  public async login(email: string, password: string): Promise<Result> {
    const loginResponse = await api.login(email, password);

    if (loginResponse.status == HttpStatusCode.Ok) {
      return new Result(true, "");
    }

    return this.mapMessageResponseToResult(loginResponse);
  }

  public async finalSalarySchemeCodeLogin(code: string): Promise<RedirectResponse> {
    return await api.finalSalarySchemeCodeLogin(code);
  }

  public async sendPasswordResetLink(email: string): Promise<Result> {
    const resetLinkResponse = await api.sendPasswordResetLink(email);
    return this.mapMessageResponseToResult(resetLinkResponse);
  }

  public async resetPassword(password: string, email: string, token: string, isAccountConfirmation: boolean): Promise<Result> {
    const resetLinkResponse = await api.resetPassword(password, email, token, isAccountConfirmation);
    return this.mapMessageResponseToResult(resetLinkResponse);
  }

  public async resetCrossover(): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
      return new Result(true);
    }

    const refreshRetireeIfOk = true;

    const resetCrossoverResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
      return await api.resetCrossover();
    }, refreshRetireeIfOk);

    return this.mapMessageResponseToResult(resetCrossoverResponse);
  }

  public async requestFinancialAdvice(name: string, email: string, phoneNo: string, ifaId: number): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
        return new Result(true);
    }

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
        return await api.requestFinancialAdvice(name, email, phoneNo, ifaId);
    }, false);

    return this.mapMessageResponseToResult(messageResponse);
  }

  public async requestIFAContact(ifaName: string, requestorName: string, phoneNumber: string): Promise<Result> {
    const requestContext = getRequestContext();

    if (requestContext.isImpersonating) {
        return new Result(true);
    }

    ifaName = ifaName.replaceAll(" ", "").replace("&", "And");

    const messageResponse = await this.asTransactionOngoing<MessageResponse>(async () => {
        return await api.requestIFAContact(ifaName, requestorName, phoneNumber);
    }, false);

    return this.mapMessageResponseToResult(messageResponse);
  }

  private async asTransactionOngoing<TReturn>(
    transactionToExecute,
    refreshRetireeIfOk: boolean
  ): Promise<TReturn> {
    store.commit("updateTransactionOngoing", true);
    let returnData: TReturn | null = null;
    await transactionToExecute().then(async response => {
      if (refreshRetireeIfOk && response.status == 200) {
        const retiree = await this.getRetiree();
        if (
          retiree != null &&
          retiree.guaranteedIncome.subscribed &&
          store.state.accordingToPlanScenario.outcome == ScenarioOutcome.Fail
        ) {
          await api.deleteGuaranteedIncome();
          await this.getRetiree();
        }
      }
      store.commit("updateTransactionOngoing", false);
      returnData = response;
    });

    if (returnData == null) {
      return Promise.resolve() as unknown as Promise<TReturn>;
    }

    return Promise.resolve(returnData as TReturn);
  }

  private isChartActiveInCurrentView(): boolean {
    return (
      store.state.view == View.Design ||
      store.state.view == View.Dashboard ||
      store.state.view == View.GuiidePlus ||
      store.state.view == View.Options
    );
  }

  private isScenarioActiveInCurrentView(scenarioType: ScenarioType): boolean {
    if (
      store.state.view == View.Dashboard ||
      store.state.view == View.GuiidePlus
    ) {
      return true;
    }

    if (
      (store.state.view == View.Design || store.state.view == View.Options) &&
      scenarioType == ScenarioType.AccordingToPlan
    ) {
      return true;
    }

    if (
      store.state.view == View.Test &&
      scenarioType != ScenarioType.AccordingToPlan
    ) {
      return true;
    }

    return false;
  }

  private isPlanActiveInCurrentView(): boolean {
    return store.state.view == View.Test || store.state.view == View.Finish || store.state.view == View.GuiidePlus || store.state.view == View.Options;
  }

  private isFinishInfoActiveInCurrentView(): boolean {
    return store.state.view == View.Finish;
  }

  private async getRetirementPlan(): Promise<RetirementPlanResponse | null> {
    const response = await api.getRetirementPlan();

    if (response.status == HttpStatusCode.Ok) {
      const retirementPlan = response.data as RetirementPlanResponse;

      for (const planYear of retirementPlan.years) {
        planYear.displayMonthDetail = false;
      }

      return retirementPlan;
    } else {
      return null;
    }
  }

  private mapMessageResponseToResult(messageResponse: MessageResponse): Result {
    if (messageResponse.status == HttpStatusCode.Ok) {
      return new Result(true, messageResponse.message);
    } else {
      return new Result(false, messageResponse.message);
    }
  }

  private createPensionProviderList(allPensionProviders: Array<PensionProviderResponse>, keysToInclude: Array<string>): Array<PensionProviderResponse> {
    const pensionProviders = new Array<PensionProviderResponse>();

    for (const key of keysToInclude) {
      const pensionProvider = allPensionProviders.find(pp => pp.key === key);
      pensionProviders.push(pensionProvider);
    }

    return pensionProviders;
  }

  private getGoogleAnalyticsUserProperties(siteName: string): Record<string, string> {
      const userProperties: Record<string, string> = {};
      const maxAnalyticsDataLength = 36;

      userProperties.siteName = siteName;

      if (store.state.retiree && store.state.retiree.finalSalaries.schemes.length > 0 && !isImpersonating()) {
        const firstScheme = store.state.retiree.finalSalaries.schemes[0];
        userProperties.aonFinalSalarySchemeId = firstScheme.id.toString();
        userProperties.aonFinalSalarySchemeName = firstScheme.name.slice(0, maxAnalyticsDataLength);
      }

      return userProperties;
  }
}

const app = new App();

export { app };
